'use client';

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { getValidationErrors, isEditFormValid, isFormValid } from '../../utils/review/validationUtils';
import { hasEditedForm, hasFormData, getInitialFormState } from '../../utils/review/formUtils';
import { transformTaxonomyData } from '../../utils/review/taxonomyUtils';
import type { ReviewFormData, ReviewFormMode, ValidationErrors } from '../../model/review/interfaces';
import type { ReviewMedia } from '../../model/review/review';
import { useProductService } from '../product/useProductService';
import { useUserService } from '../user/useUserService';

interface UseReviewFormOptions {
  isOpen: boolean;
  mode?: ReviewFormMode;
  initialValues?: Partial<ReviewFormData>;
  existingMedia?: ReviewMedia[];
}

interface UseReviewFormReturn {
  formState: ReviewFormData;
  formActions: {
    setRating: (value: number) => void;
    setTitle: (value: string) => void;
    setContent: (value: string) => void;
    setNickname: (value: string) => void;
    setEmail: (value: string) => void;
    setSkinType: (value: string) => void;
    setSkinConcern: (value: string) => void;
    setImages: (value: File[]) => void;
    setRecommend: (value: string) => void;
    setAgeGroup: (value: string) => void;
    setGender: (value: string) => void;
    setKeptMedia: (value: ReviewMedia[]) => void;
  };
  formOptions: {
    skinTypeOptions: Array<{ value: string; label: string }>;
    skinConcernOptions: Array<{ value: string; label: string }>;
  };
  hasUnsavedChanges: boolean;
  isSubmitDisabled: boolean;
  validationErrors: ValidationErrors;
  isMember: boolean;
  resetForm: () => void;
}

export const useReviewForm = ({
  isOpen,
  mode = 'create',
  initialValues,
  existingMedia,
}: UseReviewFormOptions): UseReviewFormReturn => {
  const [formState, setFormState] = useState<ReviewFormData>(getInitialFormState());
  const hasSeededRef = useRef(false);
  const { useGetProductTaxonomy } = useProductService();

  const { data: taxonomy } = useGetProductTaxonomy({
    enabled: isOpen,
  });

  const { skinTypeOptions, skinConcernOptions } = transformTaxonomyData(taxonomy);
  const { useGetUserMe } = useUserService();
  const { data: user } = useGetUserMe();

  const isMember = !!user;

  // What the modal opened showing, which is what an edit is measured against. Derived from the
  // same two inputs the seeding effect uses rather than captured from state, so it cannot drift.
  const seededFormState = useMemo(
    () => ({ ...getInitialFormState(), ...initialValues, keptMedia: existingMedia ?? [] }),
    [initialValues, existingMedia],
  );

  // Once per opening, not per render: re-seeding would overwrite the visitor's own edits.
  useEffect(() => {
    if (!isOpen) {
      hasSeededRef.current = false;
      return;
    }
    // Either input alone is worth seeding: a caller with attachments but no field values would
    // otherwise open on an empty kept-media list and save the author's photos away.
    if ((!initialValues && !existingMedia) || hasSeededRef.current) {
      return;
    }
    hasSeededRef.current = true;
    setFormState((prev) => ({ ...prev, ...initialValues, keptMedia: existingMedia ?? [] }));
  }, [isOpen, initialValues, existingMedia]);

  // `isOpen` dependency re-fills after `resetForm` empties the state on close, so a member
  // reopening the form still submits their account name/email.
  useEffect(() => {
    if (isOpen && user) {
      setFormState((prev) => ({ ...prev, nickname: user.displayName, email: user.email }));
    }
  }, [isOpen, user]);

  // Form actions for cleaner prop passing
  const formActions = useMemo(
    () => ({
      setRating: (value: number) => setFormState((prev) => ({ ...prev, rating: value })),
      setTitle: (value: string) => setFormState((prev) => ({ ...prev, title: value })),
      setContent: (value: string) => setFormState((prev) => ({ ...prev, content: value })),
      setNickname: (value: string) => setFormState((prev) => ({ ...prev, nickname: value })),
      setEmail: (value: string) => setFormState((prev) => ({ ...prev, email: value })),
      setSkinType: (value: string) => setFormState((prev) => ({ ...prev, skinType: value })),
      setSkinConcern: (value: string) => setFormState((prev) => ({ ...prev, skinConcern: value })),
      setImages: (value: File[]) => setFormState((prev) => ({ ...prev, images: value })),
      setRecommend: (value: string) => setFormState((prev) => ({ ...prev, recommend: value })),
      setAgeGroup: (value: string) => setFormState((prev) => ({ ...prev, ageGroup: value })),
      setGender: (value: string) => setFormState((prev) => ({ ...prev, gender: value })),
      setKeptMedia: (value: ReviewMedia[]) => setFormState((prev) => ({ ...prev, keptMedia: value })),
    }),
    [],
  );

  const formOptions = {
    skinTypeOptions,
    skinConcernOptions,
  };

  // Derived state
  // A member's identity is copied into formState by the effect above one render after `user`
  // resolves; block submission during that window so the payload never carries empty name/email.
  const isMemberIdentitySynced = !user || (formState.nickname === user.displayName && formState.email === user.email);
  const hasUnsavedChanges =
    mode === 'edit' ? hasEditedForm(formState, seededFormState) : hasFormData(formState, isMember);
  // An edit never sends identity or demographics, so gating it on them would leave Submit disabled
  // for good — the update endpoint has no way to receive them.
  const isSubmitDisabled =
    mode === 'edit' ? !isEditFormValid(formState) : !isFormValid(formState, isMember) || !isMemberIdentitySynced;
  // Same three inputs as the verdict above, so a message can never name a field the form would
  // have submitted without.
  const validationErrors = useMemo(() => getValidationErrors(formState, isMember, mode), [formState, isMember, mode]);

  // Reset form utility
  const resetForm = useCallback(() => {
    setFormState(getInitialFormState());
  }, []);

  return {
    formState,
    formActions,
    formOptions,
    hasUnsavedChanges,
    isSubmitDisabled,
    validationErrors,
    isMember,
    resetForm,
  };
};
